import { Window } from "@/components/Window";
import { EditorStateProvider } from "@/context/EditorStateContext";
import { useEffect, useMemo, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useSearchParams } from "react-router";
import { Editor } from "../components/Editor";
import { FileIcon } from "../components/FileIcon";
import Terminal from "../components/Terminal";

import { FilePicker } from "@/components/FilePicker/FilePicker";
import { useWindowStore } from "@/utils/windowStore";
import { DEFAULT_TEMPLATES } from "@/utils/yjs/constants";
import { useEditorStateStore } from "@/context/editorStateStore";

function EditorPage() {
	useEffect(() => {}, []);

	const [searchParams, setSearchParams] = useSearchParams();
	const template = useMemo(() => {
		const templateId = searchParams.get("template");
		if (templateId && DEFAULT_TEMPLATES[templateId]) {
			return DEFAULT_TEMPLATES[templateId];
		}
	}, [searchParams]);

	const windowState = useWindowStore();

	// Un-set the template once applied
	useEffect(() => {
		setSearchParams({});
	}, [setSearchParams]);

	const [openFiles, setOpenFiles] = useState<string[]>(
		template?.defaultOpenFiles ?? [],
	);
	const [selectedFile, setSelectedFile] = useState<string | undefined>(
		template?.defaultOpenFiles?.[0],
	);

	const { yfs } = useEditorStateStore();

	const tabs = useMemo(
		() =>
			Object.fromEntries(
				openFiles.map((file) => [
					file,
					{
						title: file.split("/").pop(),
						content: <Editor selectedFile={file} />,
						icon: (props) => <FileIcon filename={file} {...props} />,
					},
				]),
			),
		[openFiles],
	);

	return (
		<div className="m-0 bg-background w-full h-full">
			<PanelGroup direction="horizontal">
				{windowState.filePickerOpen && (
					<>
						<Panel defaultSize={10} minSize={10} order={1}>
							<FilePicker
								onFileSelected={(filePath) => {
									setOpenFiles([...openFiles, filePath]);
									setSelectedFile(filePath);
								}}
							/>
						</Panel>
						<PanelResizeHandle className="bg-border w-1" />
					</>
				)}
				<Panel defaultSize={60} order={2}>
					<Window
						tabs={tabs}
						defaultSelected={selectedFile}
						selectedFile={selectedFile}
						setSelectedFile={setSelectedFile}
						onClose={(tabId) => {
							const newFiles = openFiles.filter((file) => file !== tabId);
							setSelectedFile(newFiles[0]);
							setOpenFiles(newFiles);
						}}
					>
						{selectedFile && tabs[selectedFile] && tabs[selectedFile].content}
					</Window>
				</Panel>
				{windowState.terminalOpen && (
					<>
						<PanelResizeHandle className="bg-border w-1" />
						<Panel defaultSize={20} order={3}>
							<Terminal />
						</Panel>
					</>
				)}
			</PanelGroup>
		</div>
	);
}

export default EditorPage;
